import { useState } from "react";
import { Cliente, Propiedad, Holding, Usuario, TipoTenencia, Documento, Caso, AnalisisEstudio, CasoPropiedad, Reclamo, Cobro, Tesoreria } from "../../types";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { Badge } from "../common/Badge";
import { ClienteForm } from "./ClienteForm";
import { CambiarEstadoPropiedadModal } from "../common/CambiarEstadoPropiedadModal";
import { DocumentosClienteTab } from "./DocumentosClienteTab";

interface ClientesViewProps {
  clientes: Cliente[];
  propiedades: Propiedad[];
  holdings: Holding[];
  usuarios: Usuario[];
  usuarioActual: Usuario;
  documentos: Documento[];
  casos: Caso[];
  analisisEstudios: AnalisisEstudio[];
  casoPropiedades: CasoPropiedad[];
  reclamos: Reclamo[];
  cobros: Cobro[];
  tesoreriaItems: Tesoreria[];
  onClienteCreate: (data: Omit<Cliente, "id" | "fechaCreacion" | "fechaActualizacion">) => void;
  onClienteUpdate: (cliente: Cliente) => void;
  onClienteDelete: (id: number) => void;
  onPropiedadCreate: (data: Omit<Propiedad, "id" | "fechaCreacion">) => void;
  onPropiedadUpdate: (propiedad: Propiedad) => void;
  onPropiedadDelete: (id: number) => void;
}

export function ClientesView({
  clientes,
  propiedades,
  holdings,
  usuarios,
  usuarioActual,
  documentos,
  casos,
  analisisEstudios,
  casoPropiedades,
  reclamos,
  cobros,
  tesoreriaItems,
  onClienteCreate,
  onClienteUpdate,
  onClienteDelete,
  onPropiedadCreate,
  onPropiedadUpdate,
  onPropiedadDelete,
}: ClientesViewProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHolding, setFilterHolding] = useState<string>("all");
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [clienteToDelete, setClienteToDelete] = useState<Cliente | null>(null);

  // Inline propiedad form state
  const [isAddingProp, setIsAddingProp] = useState(false);
  const [newPropComuna, setNewPropComuna] = useState("");
  const [newPropRol, setNewPropRol] = useState("");
  const [newPropTipo, setNewPropTipo] = useState<TipoTenencia>("propia");
  const [newPropRutLeasing, setNewPropRutLeasing] = useState("");

  const filteredClientes = clientes.filter((c) => {
    const matchesSearch =
      c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.rut.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesHolding =
      filterHolding === "all" ||
      (filterHolding === "sin_holding" && !c.holdingId) ||
      (filterHolding === "con_holding" && c.holdingId);
    return matchesSearch && matchesHolding;
  });

  const getHoldingName = (holdingId?: number) => {
    if (!holdingId) return null;
    return holdings.find((h) => h.id === holdingId)?.nombre;
  };

  const getClientePropiedades = (clienteId: number) =>
    propiedades.filter((p) => p.clienteId === clienteId);

  const handleEdit = (cliente: Cliente) => {
    setEditingCliente(cliente);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (data: Omit<Cliente, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    if (editingCliente) {
      onClienteUpdate({
        ...editingCliente,
        ...data,
        fechaActualizacion: new Date().toISOString().split("T")[0],
      });
    } else {
      onClienteCreate(data);
    }
    setIsFormOpen(false);
    setEditingCliente(null);
  };

  const resetPropForm = () => {
    setIsAddingProp(false);
    setNewPropComuna("");
    setNewPropRol("");
    setNewPropTipo("propia");
    setNewPropRutLeasing("");
  };

  const handlePropSubmit = () => {
    if (!selectedCliente || !newPropComuna.trim() || !newPropRol.trim()) return;

    // Get holding name if client is assigned to one
    const holdingName = getHoldingName(selectedCliente.holdingId);

    onPropiedadCreate({
      clienteId: selectedCliente.id,
      comuna: newPropComuna.trim(),
      rol: newPropRol.trim(),
      tipoTenencia: newPropTipo,
      rutLeasing: newPropTipo === "leasing" ? newPropRutLeasing.trim() || undefined : undefined,
      nombreHolding: holdingName || undefined,
      estadoPropiedad: "activa",
    });
    resetPropForm();
  };

  const [propToDelete, setPropToDelete] = useState<Propiedad | null>(null);
  const [propToChangeEstado, setPropToChangeEstado] = useState<Propiedad | null>(null);
  const [detailTab, setDetailTab] = useState<"propiedades" | "documentos">("propiedades");
  const isAdmin = usuarioActual.rol === "admin";

  return (
    <div className="flex h-full">
      {/* Left panel - Client list */}
      <div className="w-1/2 border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Clientes</h2>
            <button
              onClick={() => { setEditingCliente(null); setIsFormOpen(true); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Nuevo Cliente
            </button>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              placeholder="Buscar por nombre o RUT..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filterHolding}
              onChange={(e) => setFilterHolding(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos</option>
              <option value="sin_holding">Sin Holding</option>
              <option value="con_holding">Asignados a Holding</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-2">
            {filteredClientes.map((cliente) => {
              const holdingName = getHoldingName(cliente.holdingId);
              const propCount = getClientePropiedades(cliente.id).length;
              return (
                <div
                  key={cliente.id}
                  onClick={() => { setSelectedCliente(cliente); resetPropForm(); }}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedCliente?.id === cliente.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-slate-800">{cliente.nombre}</h3>
                        <Badge variant={cliente.estado === "activo" ? "success" : "neutral"}>
                          {cliente.estado}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{cliente.rut}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        {holdingName ? (
                          <span className="text-blue-600">Holding: {holdingName}</span>
                        ) : (
                          <span className="text-amber-600">Sin Holding</span>
                        )}
                        <span>{propCount} Propiedades</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEdit(cliente); }}
                        className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"
                        title="Editar"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setClienteToDelete(cliente); }}
                        className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                        title="Eliminar"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredClientes.length === 0 && (
              <div className="text-center py-8 text-slate-500">
                {searchTerm || filterHolding !== "all" ? "No se encontraron clientes" : "No hay clientes registrados"}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right panel - Client detail */}
      <div className="w-1/2 p-6 overflow-y-auto">
        {selectedCliente ? (
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-semibold text-slate-800">{selectedCliente.nombre}</h2>
                <Badge variant={selectedCliente.estado === "activo" ? "success" : "neutral"}>
                  {selectedCliente.estado}
                </Badge>
              </div>
              <p className="text-slate-600">{selectedCliente.rut}</p>
              {selectedCliente.holdingId && (
                <p className="text-sm text-blue-600 mt-2">
                  Holding: {getHoldingName(selectedCliente.holdingId)}
                </p>
              )}
              {!selectedCliente.holdingId && (
                <p className="text-sm text-amber-600 mt-2">
                  Este cliente no esta asignado a ningun holding
                </p>
              )}
            </div>

            {/* Tabs: Propiedades | Documentos */}
            <div className="flex gap-1 border-b border-slate-200 mb-4">
              {(["propiedades", "documentos"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setDetailTab(tab)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    detailTab === tab
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {tab === "propiedades" ? `Propiedades (${getClientePropiedades(selectedCliente.id).length})` : "Documentos"}
                </button>
              ))}
            </div>

            {detailTab === "documentos" && (
              <DocumentosClienteTab
                clienteId={selectedCliente.id}
                propiedades={propiedades}
                documentos={documentos}
                casos={casos}
                analisisEstudios={analisisEstudios}
                casoPropiedades={casoPropiedades}
                reclamos={reclamos}
                cobros={cobros}
                tesoreriaItems={tesoreriaItems}
                usuarios={usuarios}
              />
            )}

            {/* Propiedades */}
            {detailTab === "propiedades" && <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-slate-700">
                  Propiedades ({getClientePropiedades(selectedCliente.id).length})
                </h3>
                {!isAddingProp && (
                  <button
                    onClick={() => setIsAddingProp(true)}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    + Agregar Propiedad
                  </button>
                )}
              </div>

              {/* Add propiedad form */}
              {isAddingProp && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={newPropComuna}
                      onChange={(e) => setNewPropComuna(e.target.value)}
                      placeholder="Comuna"
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={newPropRol}
                      onChange={(e) => setNewPropRol(e.target.value)}
                      placeholder="ROL (ej: 1234-56)"
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={newPropTipo}
                    onChange={(e) => setNewPropTipo(e.target.value as TipoTenencia)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="propia">Propia</option>
                    <option value="leasing">Leasing</option>
                    <option value="concesion">Concesion</option>
                  </select>
                  {newPropTipo === "leasing" && (
                    <input
                      type="text"
                      value={newPropRutLeasing}
                      onChange={(e) => setNewPropRutLeasing(e.target.value)}
                      placeholder="RUT Leasing (ej: 76.999.888-7)"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={resetPropForm}
                      className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handlePropSubmit}
                      disabled={!newPropComuna.trim() || !newPropRol.trim()}
                      className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-slate-300"
                    >
                      Agregar
                    </button>
                  </div>
                </div>
              )}

              {getClientePropiedades(selectedCliente.id).length > 0 ? (
                <div className="space-y-2">
                  {getClientePropiedades(selectedCliente.id).map((p) => (
                    <div key={p.id} className={`p-3 rounded-lg border flex justify-between items-center ${
                      p.estadoPropiedad === "activa" ? "bg-white border-slate-200" :
                      p.estadoPropiedad === "desvinculada" ? "bg-amber-50 border-amber-200" :
                      "bg-red-50 border-red-200"
                    }`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm text-slate-800">
                            ROL {p.rol} - {p.comuna}
                          </p>
                          {p.estadoPropiedad !== "activa" && (
                            <Badge variant={p.estadoPropiedad === "desvinculada" ? "warning" : "error"}>
                              {p.estadoPropiedad}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {p.tipoTenencia === "concesion" ? "Concesion" : p.tipoTenencia === "leasing" ? `Leasing${p.rutLeasing ? ` - RUT: ${p.rutLeasing}` : ""}` : "Propia"}
                        </p>
                        {p.estadoPropiedad !== "activa" && (
                          isAdmin ? (
                            <div className="mt-1 text-xs text-slate-500">
                              {p.motivoEstado && <p>Motivo: {p.motivoEstado}</p>}
                              {p.fechaCambioEstado && <p>Desde: {p.fechaCambioEstado}</p>}
                            </div>
                          ) : (
                            <p className="mt-1 text-xs text-slate-400">
                              ROL {p.estadoPropiedad} {p.fechaCambioEstado ? `el ${p.fechaCambioEstado}` : ""}
                            </p>
                          )
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {isAdmin && (
                          <button
                            onClick={() => setPropToChangeEstado(p)}
                            className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                            title="Cambiar estado"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => setPropToDelete(p)}
                          className="p-1 text-slate-400 hover:text-red-600 transition-colors"
                          title="Eliminar propiedad"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4 bg-white rounded-lg border border-slate-200">
                  No hay propiedades
                </p>
              )}
            </div>}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            Selecciona un cliente para ver detalles
          </div>
        )}
      </div>

      {/* Modal Nuevo/Editar Cliente */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingCliente(null); }}
        title={editingCliente ? "Editar Cliente" : "Nuevo Cliente"}
      >
        <ClienteForm
          cliente={editingCliente}
          usuarios={usuarios}
          onSubmit={handleFormSubmit}
          onCancel={() => { setIsFormOpen(false); setEditingCliente(null); }}
        />
      </Modal>

      {/* Confirm eliminar Cliente */}
      <ConfirmDialog
        isOpen={clienteToDelete !== null}
        onClose={() => setClienteToDelete(null)}
        onConfirm={() => {
          if (clienteToDelete) {
            onClienteDelete(clienteToDelete.id);
            if (selectedCliente?.id === clienteToDelete.id) setSelectedCliente(null);
          }
          setClienteToDelete(null);
        }}
        title="Eliminar Cliente"
        message={`Esta seguro que desea eliminar el cliente "${clienteToDelete?.nombre}"?`}
        confirmText="Eliminar"
      />

      {/* Confirm eliminar Propiedad */}
      <ConfirmDialog
        isOpen={propToDelete !== null}
        onClose={() => setPropToDelete(null)}
        onConfirm={() => {
          if (propToDelete) onPropiedadDelete(propToDelete.id);
          setPropToDelete(null);
        }}
        title="Eliminar Propiedad"
        message={`Eliminar ROL ${propToDelete?.rol} - ${propToDelete?.comuna}?`}
        confirmText="Eliminar"
      />

      {/* Modal Cambiar Estado Propiedad */}
      <Modal
        isOpen={propToChangeEstado !== null}
        onClose={() => setPropToChangeEstado(null)}
        title="Cambiar Estado de ROL"
      >
        {propToChangeEstado && (
          <CambiarEstadoPropiedadModal
            propiedad={propToChangeEstado}
            onSubmit={(updated) => {
              onPropiedadUpdate(updated);
              setPropToChangeEstado(null);
            }}
            onCancel={() => setPropToChangeEstado(null)}
          />
        )}
      </Modal>
    </div>
  );
}
